// StudyDost — Teacher Dashboard
// frontend/src/pages/TeacherDashboard.js
// Route: /teacher/dashboard

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Users, BookOpen, TrendingUp, Copy, Check,
  QrCode, Share2, LogOut, BarChart2, Flame
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import HomeworkGenerator from './HomeworkGenerator';

const SUBJECT_CONFIG = {
  MATH:     { emoji: '🧮', color: '#4F46E5', label: 'Math' },
  PHYSICS:  { emoji: '⚡', color: '#0891B2', label: 'Physics' },
  CHEMISTRY:{ emoji: '🧪', color: '#D97706', label: 'Chemistry' },
  BIOLOGY:  { emoji: '🌿', color: '#059669', label: 'Biology' },
  SCIENCE:  { emoji: '🔬', color: '#7C3AED', label: 'Science' },
  ENGLISH:  { emoji: '📖', color: '#BE185D', label: 'English' },
  HINDI:    { emoji: '🔤', color: '#C2410C', label: 'Hindi' },
  HISTORY:  { emoji: '📜', color: '#92400E', label: 'History' },
  GEOGRAPHY:{ emoji: '🗺️', color: '#065F46', label: 'Geography' },
  OTHER:    { emoji: '📝', color: '#475569', label: 'General' },
};

export default function TeacherDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const { data, isLoading } = useQuery({
    queryKey: ['teacherDashboard'],
    queryFn: () => api.get('/teacher/dashboard').then(r => r.data),
    refetchInterval: false,
    retry: false,
  });

  const { data: earningsData } = useQuery({
    queryKey: ['teacher-earnings'],
    queryFn: () => api.get('/teacher/earnings').then(r => r.data),
    staleTime: 2 * 60 * 1000,
  });

  const { data: referralInfo, error: referralError } = useQuery({
    queryKey: ['teacherReferral'],
    queryFn: () => api.get('/teacher/referral-info').then(r => r.data),
    retry: false,
    staleTime: 5 * 60 * 1000, // don't re-fetch for 5 mins
  });
  const profileMissing = referralError?.response?.status === 404 || referralError?.response?.status === 401;

  const { data: analyticsData } = useQuery({
    queryKey: ['teacherAnalytics'],
    queryFn: () => api.get('/teacher/analytics?days=7').then(r => r.data),
    enabled: activeTab === 'analytics',
  });

  const handleCopyCode = () => {
    navigator.clipboard.writeText(referralInfo?.referralCode || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareLink = () => {
    if (navigator.share && referralInfo?.whatsappDeepLink) {
      navigator.share({
        title: 'Join StudyDost — Free AI Tutor',
        text: `Join my class on StudyDost! Use code ${referralInfo.referralCode} for 1 month FREE.\n\nClick to open WhatsApp:`,
        url: referralInfo.whatsappDeepLink,
      });
    } else {
      navigator.clipboard.writeText(referralInfo?.whatsappDeepLink || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isLoading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner} />
        <p style={{ color: '#6B7280', marginTop: 12 }}>Loading dashboard...</p>
      </div>
    );
  }

  const teacher = data?.teacher;
  const stats = data?.stats;
  const students = data?.students || [];

  return (
    <div style={styles.page}>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerBg} />
        <div style={styles.headerContent}>
          <div style={styles.headerTop}>
            <div>
              <div style={styles.headerGreeting}>🎓 Teacher Dashboard</div>
              <h1 style={styles.headerName}>{teacher?.name || user?.name || 'Teacher'}</h1>
              <p style={styles.headerSchool}>
                🏫 {teacher?.schoolName} · {teacher?.board}
              </p>
            </div>
            <button style={styles.logoutBtn} onClick={() => { logout(); navigate('/'); }}>
              <LogOut size={16} />
            </button>
          </div>

          {/* Quick stats */}
          <div style={styles.headerStats}>
            {[
              { value: teacher?.studentCount || 0, label: 'Students' },
              { value: stats?.activeThisWeek || 0, label: 'Active this week' },
              { value: stats?.totalQuestionsThisWeek || 0, label: 'Questions asked' },
            ].map((s, i) => (
              <div key={i} style={styles.headerStat}>
                <div style={styles.headerStatNum}>{s.value}</div>
                <div style={styles.headerStatLabel}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={styles.tabBar}>
        {[
          { id: 'overview', label: '📋 Overview' },
          { id: 'students', label: '👥 Students' },
          { id: 'analytics', label: '📊 Analytics' },
          { id: 'referral', label: '🔗 Referral' },
          { id: 'earnings', label: '💰 Earnings' },
          { id: 'homework', label: '📝 Homework' },
        ].map(tab => (
          <button
            key={tab.id}
            style={{
              ...styles.tab,
              borderBottom: activeTab === tab.id ? '2px solid #4F46E5' : '2px solid transparent',
              color: activeTab === tab.id ? '#4F46E5' : '#64748B',
              fontWeight: activeTab === tab.id ? 700 : 500,
            }}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={styles.content}>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && (
          <>
            {/* Profile missing — prompt to complete onboarding */}
            {profileMissing && (
              <div style={{background:'#FFF7ED',border:'2px solid #FB923C',borderRadius:16,padding:'20px 24px',marginBottom:16,textAlign:'center'}}>
                <div style={{fontSize:32,marginBottom:8}}>⚠️</div>
                <div style={{fontWeight:700,color:'#9A3412',marginBottom:6}}>Profile setup incomplete</div>
                <p style={{color:'#7C2D12',fontSize:14,marginBottom:16}}>Aapka teacher profile create nahi hua. Please onboarding complete karein.</p>
                <button
                  style={{background:'#EA580C',color:'#FFF',border:'none',borderRadius:10,padding:'10px 20px',fontWeight:700,cursor:'pointer'}}
                  onClick={() => navigate('/teacher/setup')}
                >Complete Setup →</button>
              </div>
            )}
            {/* Referral code card */}
            <div style={styles.referralCard}>
              <div style={styles.referralLeft}>
                <div style={styles.referralLabel}>Your Referral Code</div>
                <div style={styles.referralCode}>
                  {referralInfo?.referralCode || (profileMissing ? 'Setup incomplete' : '...')}
                </div>
                <div style={styles.referralNote}>
                  Students get 1 month FREE with this code!
                </div>
              </div>
              <div style={styles.referralActions}>
                <button style={styles.copyBtn} onClick={handleCopyCode}>
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
                <button style={styles.shareBtn} onClick={handleShareLink}>
                  <Share2 size={16} />
                </button>
              </div>
            </div>

            {/* Blackboard text */}
            {referralInfo?.blackboardText && (
              <div style={styles.blackboard}>
                <div style={styles.blackboardLabel}>📋 Blackboard pe likhein:</div>
                <pre style={styles.blackboardText}>{referralInfo.blackboardText}</pre>
              </div>
            )}

            {/* Subject breakdown */}
            {stats?.subjectBreakdown?.length > 0 && (
              <div style={styles.section}>
                <div style={styles.sectionTitle}>
                  <BookOpen size={14} color="#4F46E5" />
                  Class ke questions — Subject wise (Last 30 days)
                </div>
                {stats.subjectBreakdown.map((s, i) => {
                  const config = SUBJECT_CONFIG[s.subject] || SUBJECT_CONFIG.OTHER;
                  const max = stats.subjectBreakdown[0]?._count?.subject || 1;
                  return (
                    <div key={i} style={styles.subjectRow}>
                      <span style={styles.subjectEmoji}>{config.emoji}</span>
                      <span style={styles.subjectName}>{config.label}</span>
                      <div style={styles.subjectBarTrack}>
                        <div style={{
                          ...styles.subjectBarFill,
                          width: `${(s._count.subject / max) * 100}%`,
                          background: config.color,
                        }} />
                      </div>
                      <span style={styles.subjectCount}>{s._count.subject}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── STUDENTS TAB ── */}
        {activeTab === 'students' && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <Users size={14} color="#4F46E5" />
              {students.length} Students in your class
            </div>
            {students.length === 0 ? (
              <div style={styles.emptyStudents}>
                <p style={{ fontSize: 14, color: '#94A3B8', textAlign: 'center' }}>
                  No students yet. Share your referral code with your class!
                </p>
              </div>
            ) : (
              students.map((student, i) => (
                <div key={student.id} style={styles.studentRow}>
                  <div style={styles.studentRank}>{i + 1}</div>
                  <div style={styles.studentAvatar}>
                    {(student.name || 'S')[0].toUpperCase()}
                  </div>
                  <div style={styles.studentInfo}>
                    <div style={styles.studentName}>{student.name || 'Student'}</div>
                    <div style={styles.studentMeta}>
                      Class {student.class} · {student.questionsThisWeek} questions this week
                    </div>
                  </div>
                  <div style={styles.studentRight}>
                    {student.streak > 0 && (
                      <span style={styles.streakBadge}>
                        🔥 {student.streak}
                      </span>
                    )}
                    <div style={{
                      ...styles.planDot,
                      background: student.plan === 'FREE' ? '#94A3B8' : '#4F46E5',
                    }} title={student.plan} />
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ── ANALYTICS TAB ── */}
        {activeTab === 'analytics' && analyticsData && (
          <>
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <BarChart2 size={14} color="#4F46E5" />
                Daily Activity (Last 14 days)
              </div>
              <div style={styles.barChart}>
                {analyticsData.dailyActivity?.slice(-7).map((day, i) => {
                  const max = Math.max(...analyticsData.dailyActivity.map(d => d.count), 1);
                  const height = Math.max((day.count / max) * 80, 4);
                  const label = new Date(day.date).toLocaleDateString('en-IN', { weekday: 'short' });
                  return (
                    <div key={i} style={styles.barCol}>
                      <div style={styles.barCount}>{day.count || ''}</div>
                      <div style={{ ...styles.bar, height, background: day.count > 0 ? '#4F46E5' : '#E2E8F0' }} />
                      <div style={styles.barLabel}>{label}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <TrendingUp size={14} color="#4F46E5" />
                Recent Questions from Class
              </div>
              {analyticsData.topQuestions?.slice(0, 10).map((q, i) => (
                <div key={i} style={styles.questionRow}>
                  <span style={styles.questionNum}>{i + 1}</span>
                  <div style={styles.questionText}>
                    {q.questionText.slice(0, 80)}{q.questionText.length > 80 ? '...' : ''}
                  </div>
                  <span style={{
                    ...styles.subjectPill,
                    background: (SUBJECT_CONFIG[q.subject]?.color || '#94A3B8') + '15',
                    color: SUBJECT_CONFIG[q.subject]?.color || '#94A3B8',
                  }}>
                    {SUBJECT_CONFIG[q.subject]?.emoji} {SUBJECT_CONFIG[q.subject]?.label || 'General'}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── REFERRAL TAB ── */}
        {/* ── EARNINGS TAB ── */}
        {activeTab === 'earnings' && (
          <div>
            {/* Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div style={{ background: '#F0FDF4', border: '1.5px solid #86EFAC', borderRadius: 14, padding: '16px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#15803D' }}>
                  ₹{earningsData?.totalEarned || 0}
                </div>
                <div style={{ fontSize: 12, color: '#166534', marginTop: 2 }}>Total Earned</div>
              </div>
              <div style={{ background: earningsData?.reachedThreshold ? '#FFF7ED' : '#F8FAFF', border: `1.5px solid ${earningsData?.reachedThreshold ? '#FB923C' : '#C7D2FE'}`, borderRadius: 14, padding: '16px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: earningsData?.reachedThreshold ? '#C2410C' : '#4F46E5' }}>
                  ₹{earningsData?.pendingPayout || 0}
                </div>
                <div style={{ fontSize: 12, color: earningsData?.reachedThreshold ? '#9A3412' : '#4338CA', marginTop: 2 }}>
                  {earningsData?.reachedThreshold ? '🎉 Payout Ready!' : 'Pending Payout'}
                </div>
              </div>
            </div>

            {/* Progress to threshold */}
            <div style={{ background: '#FFF', border: '1.5px solid #E2E8F0', borderRadius: 14, padding: '16px 14px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Payout Progress</span>
                <span style={{ fontSize: 13, color: '#6B7280' }}>
                  ₹{earningsData?.pendingPayout || 0} / ₹{earningsData?.payoutThreshold || 1000}
                </span>
              </div>
              <div style={{ background: '#E2E8F0', borderRadius: 8, height: 10, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  borderRadius: 8,
                  background: 'linear-gradient(90deg, #4F46E5, #7C3AED)',
                  width: `${Math.min(((earningsData?.pendingPayout || 0) / (earningsData?.payoutThreshold || 1000)) * 100, 100)}%`,
                  transition: 'width 0.5s ease',
                }} />
              </div>
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>
                Payout ₹{earningsData?.payoutThreshold || 1000} hone par milega (UPI transfer)
              </div>
            </div>

            {/* How it works */}
            <div style={{ background: '#F5F3FF', border: '1.5px solid #DDD6FE', borderRadius: 14, padding: '16px 14px', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: '#4C1D95', marginBottom: 10, fontSize: 14 }}>💡 How it works</div>
              <div style={{ fontSize: 13, color: '#5B21B6', lineHeight: 1.7 }}>
                • Har student jo aapke code se join karta hai aur paid plan leta hai<br/>
                • Aapko milenge <strong>₹50 per student</strong> (one-time)<br/>
                • ₹1,000 balance hone par UPI transfer milega<br/>
                • Currently: <strong>{earningsData?.paidConversions || 0} paid conversions</strong> out of {earningsData?.totalReferredStudents || 0} joined
              </div>
            </div>

            {/* Paid conversions list */}
            {earningsData?.earnings?.length > 0 ? (
              <div style={{ background: '#FFF', border: '1.5px solid #E2E8F0', borderRadius: 14, padding: '14px', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, color: '#374151', marginBottom: 10, fontSize: 13 }}>
                  ✅ Paid Conversions ({earningsData.earnings.length})
                </div>
                {earningsData.earnings.map((e, i) => (
                  <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < earningsData.earnings.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Student #{i + 1}</div>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>{new Date(e.createdAt).toLocaleDateString('en-IN')}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#15803D' }}>+₹{e.amountPaise / 100}</div>
                      <div style={{ fontSize: 11, color: e.status === 'PAID' ? '#15803D' : '#F59E0B', fontWeight: 600 }}>
                        {e.status === 'PAID' ? '✅ Paid' : '⏳ Pending'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '32px 16px', color: '#94A3B8', fontSize: 14 }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>💰</div>
                Abhi tak koi paid conversion nahi.<br/>
                Apna referral code share karein!
              </div>
            )}

            {earningsData?.lastPayoutAt && (
              <div style={{ fontSize: 12, color: '#94A3B8', textAlign: 'center' }}>
                Last payout: {new Date(earningsData.lastPayoutAt).toLocaleDateString('en-IN')}
              </div>
            )}
          </div>
        )}

        {activeTab === 'homework' && (
          <HomeworkGenerator
            token={localStorage.getItem('studydost_token')}
            teacherProfile={teacher}
          />
        )}

        {activeTab === 'referral' && (
          <>
            <div style={styles.referralBig}>
              <div style={styles.referralBigCode}>
                {referralInfo?.referralCode || '...'}
              </div>
              <div style={styles.referralBigLabel}>Your Referral Code</div>
              <div style={styles.referralBigStats}>
                <div style={styles.referralStat}>
                  <div style={styles.referralStatNum}>{teacher?.studentCount || 0}</div>
                  <div style={styles.referralStatLabel}>Students Joined</div>
                </div>
                <div style={styles.referralStatDiv} />
                <div style={styles.referralStat}>
                  <div style={styles.referralStatNum}>1 Month</div>
                  <div style={styles.referralStatLabel}>Free for each student</div>
                </div>
              </div>
            </div>

            <div style={styles.section}>
              <div style={styles.sectionTitle}>How to Share</div>

              <div style={styles.shareMethod}>
                <div style={styles.shareMethodNum}>1</div>
                <div>
                  <div style={styles.shareMethodTitle}>📋 Blackboard pe likhein</div>
                  <div style={styles.shareMethodDesc}>Easiest for classroom</div>
                  <div style={styles.blackboard}>
                    <pre style={styles.blackboardText}>{referralInfo?.blackboardText}</pre>
                  </div>
                </div>
              </div>

              <div style={styles.shareMethod}>
                <div style={styles.shareMethodNum}>2</div>
                <div style={{ flex: 1 }}>
                  <div style={styles.shareMethodTitle}>🔗 WhatsApp Deep Link</div>
                  <div style={styles.shareMethodDesc}>Send to class WhatsApp group</div>
                  <button style={styles.shareLinkBtn} onClick={handleShareLink}>
                    <Share2 size={16} />
                    Share Link (Opens WhatsApp with code)
                  </button>
                </div>
              </div>

              <div style={styles.shareMethod}>
                <div style={styles.shareMethodNum}>3</div>
                <div>
                  <div style={styles.shareMethodTitle}>💬 Code manually type karein</div>
                  <div style={styles.shareMethodDesc}>Students type code in StudyDost chat</div>
                  <button style={styles.copyCodeBtn} onClick={handleCopyCode}>
                    {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy Code</>}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#F8FAFF', fontFamily: "'DM Sans', sans-serif", paddingBottom: 80 },
  loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' },
  spinner: { width: 36, height: 36, border: '3px solid #E2E8F0', borderTopColor: '#4F46E5', borderRadius: '50%', animation: 'spin 0.7s linear infinite' },

  header: { position: 'relative', overflow: 'hidden', padding: '28px 20px 32px' },
  headerBg: { position: 'absolute', inset: 0, background: 'linear-gradient(160deg, #1E3A5F 0%, #0F172A 100%)' },
  headerContent: { position: 'relative', zIndex: 1 },
  headerTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  headerGreeting: { fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 },
  headerName: { fontFamily: "'Sora', sans-serif", fontSize: 24, fontWeight: 800, color: '#FFFFFF', marginBottom: 4 },
  headerSchool: { fontSize: 13, color: 'rgba(255,255,255,0.6)' },
  logoutBtn: { background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: 8, cursor: 'pointer', color: '#FFFFFF', display: 'flex' },
  headerStats: { display: 'flex', gap: 0 },
  headerStat: { flex: 1, background: 'rgba(255,255,255,0.1)', borderRadius: 12, padding: '10px', textAlign: 'center', margin: '0 4px' },
  headerStatNum: { fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 800, color: '#FFFFFF' },
  headerStatLabel: { fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 2 },

  tabBar: { display: 'flex', background: '#FFFFFF', borderBottom: '1px solid #F1F5F9', overflowX: 'auto' },
  tab: { flex: 1, padding: '12px 8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.2s' },

  content: { padding: '16px' },

  referralCard: { background: 'linear-gradient(135deg, #4F46E5, #7C3AED)', borderRadius: 18, padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  referralLeft: {},
  referralLabel: { fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 },
  referralCode: { fontFamily: 'monospace', fontSize: 32, fontWeight: 900, color: '#FFFFFF', letterSpacing: '0.15em', marginBottom: 6 },
  referralNote: { fontSize: 12, color: 'rgba(255,255,255,0.7)' },
  referralActions: { display: 'flex', flexDirection: 'column', gap: 8 },
  copyBtn: { background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 10, padding: '10px', cursor: 'pointer', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  shareBtn: { background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 10, padding: '10px', cursor: 'pointer', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' },

  blackboard: { background: '#1A2632', borderRadius: 10, padding: '12px 14px', marginTop: 10 },
  blackboardLabel: { fontSize: 12, color: '#94A3B8', marginBottom: 8, fontWeight: 600 },
  blackboardText: { fontFamily: 'monospace', fontSize: 13, color: '#E2E8F0', lineHeight: 1.8, whiteSpace: 'pre-wrap', margin: 0 },

  section: { background: '#FFFFFF', borderRadius: 16, padding: '16px', marginBottom: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)' },
  sectionTitle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.06em' },

  subjectRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  subjectEmoji: { fontSize: 16, width: 20, flexShrink: 0 },
  subjectName: { fontSize: 13, color: '#374151', width: 72, flexShrink: 0 },
  subjectBarTrack: { flex: 1, height: 8, background: '#F1F5F9', borderRadius: 4, overflow: 'hidden' },
  subjectBarFill: { height: '100%', borderRadius: 4 },
  subjectCount: { fontSize: 13, fontWeight: 700, color: '#374151', width: 28, textAlign: 'right' },

  studentRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #F8FAFF' },
  studentRank: { width: 20, fontSize: 12, color: '#94A3B8', fontWeight: 700, flexShrink: 0 },
  studentAvatar: { width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #4F46E5, #7C3AED)', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 },
  studentInfo: { flex: 1 },
  studentName: { fontSize: 14, fontWeight: 600, color: '#0F172A' },
  studentMeta: { fontSize: 11, color: '#94A3B8', marginTop: 2 },
  studentRight: { display: 'flex', alignItems: 'center', gap: 8 },
  streakBadge: { fontSize: 12, fontWeight: 700, color: '#EA580C' },
  planDot: { width: 8, height: 8, borderRadius: '50%' },
  emptyStudents: { padding: '20px 0' },

  barChart: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: 100, gap: 4 },
  barCol: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  barCount: { fontSize: 10, color: '#94A3B8', height: 14 },
  bar: { width: '100%', borderRadius: '4px 4px 0 0' },
  barLabel: { fontSize: 9, color: '#94A3B8' },

  questionRow: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid #F8FAFF' },
  questionNum: { width: 20, fontSize: 12, color: '#94A3B8', fontWeight: 700, flexShrink: 0, paddingTop: 2 },
  questionText: { flex: 1, fontSize: 13, color: '#374151', lineHeight: 1.4 },
  subjectPill: { flexShrink: 0, fontSize: 10, fontWeight: 700, padding: '3px 7px', borderRadius: 100 },

  referralBig: { background: 'linear-gradient(135deg, #0F172A, #1E3A5F)', borderRadius: 20, padding: '28px 20px', textAlign: 'center', marginBottom: 16 },
  referralBigCode: { fontFamily: 'monospace', fontSize: 42, fontWeight: 900, color: '#FFFFFF', letterSpacing: '0.2em', marginBottom: 8 },
  referralBigLabel: { fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 20 },
  referralBigStats: { display: 'flex', justifyContent: 'center', gap: 0, background: 'rgba(255,255,255,0.08)', borderRadius: 14, padding: '14px' },
  referralStat: { flex: 1, textAlign: 'center' },
  referralStatNum: { fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 800, color: '#FFFFFF' },
  referralStatLabel: { fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 4 },
  referralStatDiv: { width: 1, background: 'rgba(255,255,255,0.15)', margin: '0 16px' },

  shareMethod: { display: 'flex', gap: 14, marginBottom: 20, alignItems: 'flex-start' },
  shareMethodNum: { width: 28, height: 28, borderRadius: '50%', background: '#EEF2FF', color: '#4F46E5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 },
  shareMethodTitle: { fontSize: 14, fontWeight: 700, color: '#0F172A', marginBottom: 4 },
  shareMethodDesc: { fontSize: 12, color: '#94A3B8', marginBottom: 10 },
  shareLinkBtn: { display: 'flex', alignItems: 'center', gap: 8, background: '#25D366', color: '#FFFFFF', border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  copyCodeBtn: { display: 'flex', alignItems: 'center', gap: 6, background: '#EEF2FF', color: '#4F46E5', border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};
